import { Backdrop } from "@material-ui/core";
import { useUser } from "@saleor/auth";
import MyTypography from "@saleor/components/MyTypography";
import SnackbarAlert from "@saleor/components/SnackbarAlert";
import {
  useScgpUserCheckValidTokenResetPasswordMutation,
  useScgpUserConfirmResetPasswordMutation
} from "@saleor/graphql";
import useNavigator from "@saleor/hooks/useNavigator";
import { SetPasswordData } from "@saleor/sdk";
import { Auth } from "aws-amplify";
import { parse as parseQs } from "qs";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { RouteComponentProps } from "react-router";

import NewPasswordPage, {
  NewPasswordPageFormData
} from "../components/NewPasswordPage";
import useStyles from "../components/styles";
import { passwordResetInvalidPath } from "../urls";
import { setTokens } from "../utils";

const NewPassword: React.FC<RouteComponentProps> = ({ location }) => {
  const qs = parseQs(location.search.substr(1));
  const { loginByToken } = useUser();
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [errors, setErrors] = useState<SetPasswordData["errors"]>([]);
  const [errorCognito, setErrorCognito] = useState<string>("");
  const navigate = useNavigator();
  const intl = useIntl();
  const classes = useStyles();
  const userLogin = useMemo(() => sessionStorage.getItem("user"), []);
  const [checkToken] = useScgpUserCheckValidTokenResetPasswordMutation({
    onCompleted: data => {
      if (data?.scgpUserCheckValidTokenResetPassword?.errors?.length) {
        navigate(passwordResetInvalidPath);
      }
    }
  });
  useEffect(() => {
    if (qs.email && qs.token) {
      checkToken({
        variables: {
          email: qs.email,
          token: qs.token
        }
      });
      return;
    }
    navigate(passwordResetInvalidPath);
  }, []);
  const [resetPassword] = useScgpUserConfirmResetPasswordMutation({
    onCompleted: data => {
      if (!data?.scgpUserConfirmResetPassword?.errors?.length) {
        setOpenAlert(true);
        return;
      }
      setErrors(
        data.scgpUserConfirmResetPassword.errors as SetPasswordData["errors"]
      );
    }
  });

  const handleLoginWithToken = async sesstion => {
    const results = await loginByToken(sesstion.idToken.jwtToken);
    setTokens(results?.token, results?.csrfToken, results?.refreshToken, false);
    setLoading(false);
    navigate("/", { replace: true });
  };
  const handleSubmit = async (data: NewPasswordPageFormData) => {
    setLoading(true);
    if (userLogin) {
      const userArr = userLogin.split(",");

      const user = await Auth.signIn(userArr[0], userArr[1]);
      if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
        Auth.completeNewPassword(user, data.password)
          .then(() => {
            Auth.currentSession().then(dataUser =>
              handleLoginWithToken(dataUser)
            );
          })
          .catch(e => {
            setErrorCognito(e.message);
          });
      }
    } else {
      resetPassword({
        variables: {
          new_password: data.password,
          confirm_password: data.confirmPassword,
          email: qs?.email || null,
          token: qs?.token || null
        }
      });
    }
  };
  const handleClose = () => {
    navigate("/");
    setOpenAlert(false);
  };
  return (
    <>
      <NewPasswordPage
        errors={errors}
        errorCognito={errorCognito}
        disabled={loading}
        onBack={() => navigate("/")}
        onSubmit={handleSubmit}
      />
      <SnackbarAlert
        severity="success"
        open={openAlert}
        onClose={handleClose}
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
        verticalCenter
      >
        <div className={classes.snackHeadTitle}>
          <MyTypography
            variant="subtitle2"
            style={{ color: "#ffffff", marginRight: "2px" }}
          >
            {intl.formatMessage({
              defaultMessage: "Reset password successfully.",
              description: "dialog header"
            })}
          </MyTypography>
        </div>
      </SnackbarAlert>
      <Backdrop open={openAlert} className={classes.backDrop} />
    </>
  );
};

NewPassword.displayName = "NewPassword";
export default NewPassword;
